import React, { useMemo } from 'react';
import DashedLine from '@components/DashedLine';
import * as styles from './RateModal.module.scss';
import useConfig from '@hooks/useConfig';
import { isFunction, round } from 'lodash';
import classNames from 'classnames';
import { keys } from '@enums';
import {
  formatCurrency,
  formatPercent,
  calculatePercent,
  formatParagraphs,
  isRefinance
} from '@helpers';

const types = {
  CURRENCY: 'currency',
  PERCENT: 'percent'
};

export default React.memo(function SampleLoan({ product, loanDetails }) {
  const data = useMemo(() => {
    if (!product || !loanDetails) {
      return null;
    }

    const cashOutAmount = round(loanDetails.cashOutAmount, 2);

    const newLoanDetails = {
      ...loanDetails,
      loanAmount: round(loanDetails.loanAmount, 2),
      homeValue: round(loanDetails.homeValue, 2),
      cashOutAmount:
        cashOutAmount &&
        !isNaN(cashOutAmount) &&
        isRefinance(loanDetails.loanPurpose)
          ? cashOutAmount
          : 0
    };

    return { ...product, ...newLoanDetails };
  }, [product, loanDetails]);

  const rows = sharedRows.concat(product?.isAdjustable ? adjustableRows : []);

  return !data ? null : (
    <div
      className={styles.sampleLoanWrapper}
      tabIndex={-1}
    >
      <div className={styles.sampleLoanContent}>
        <h3>Your loan details</h3>

        <div className={styles.sampleLoan}>
          {rows.map((row, index) => {
            const value = isFunction(row.value)
              ? row.value(data)
              : data[row.key];

            return (
              <SampleLoanRow
                key={index}
                name={row.key}
                label={row.label}
                value={value}
                type={row.type}
                data={data}
                addDecimals={row.addDecimals}
              />
            );
          })}
          <PaymentRow product={product} />
        </div>
        <SampleLoanDisclosure product={product} />
      </div>
    </div>
  );
});

function SampleLoanDisclosure({ product }) {
  const config = useConfig();

  if (!product) {
    return null;
  }

  const text = (
    product.isAdjustable
      ? config.sampleLoanDisclosureAdjustable
      : config.sampleLoanDisclosureFixed
  )
    ?.replace(/<br\s?\/?>/g, '\n')
    .replace(/&nbsp;/g, '')
    .trim();

  return !text ? null : (
    <div className={styles.sampleLoanDisclosure}>{formatParagraphs(text)}</div>
  );
}

function SampleLoanRow({
  className,
  name,
  label,
  value,
  type,
  children,
  data,
  addDecimals = true
}) {
  if (type && !React.isValidElement(value)) {
    value =
      type === types.CURRENCY
        ? formatCurrency(value, addDecimals)
        : formatPercent(value, addDecimals);
  }

  return (
    <div className={classNames(styles.row, className)}>
      <div className={styles.rowContent}>
        {children ?? (
          <>
            <div className={styles.label}>{label}</div>
            <div className={styles.value}>{value}</div>
          </>
        )}
      </div>
      {name === keys.LOAN_AMOUNT && <CashOutDetails data={data} />}
      <DashedLine
        direction="horizontal"
        color="#CDD7E5"
        className={styles.divider}
      />
    </div>
  );
}

function CashOutDetails({ data }) {
  return !isRefinance(data.loanPurpose) || data.cashOutAmount <= 0 ? null : (
    <div className={styles.details}>
      <strong>{formatCurrency(data.loanAmount, true)}</strong> loan{' '}
      <span className={styles.plus}>+</span>{' '}
      <strong>{formatCurrency(data.cashOutAmount, true)}</strong> cash out
    </div>
  );
}

function PaymentRow({ product }) {
  const label = 'Monthly Principal & Interest Payments*';

  return (
    <SampleLoanRow
      className={classNames({
        [styles.adjustablePayments]: product.isAdjustable
      })}
    >
      <div className={styles.label}>{label}</div>
      <div className={styles.value}>
        {!product.isAdjustable ? (
          <>
            {product.months} payments of {formatCurrency(product.payment, true)}
          </>
        ) : (
          <div className={styles.paymentsWrapper}>
            {getAdjustablePayments(product)}
          </div>
        )}
      </div>
    </SampleLoanRow>
  );
}

function getAdjustablePayments(product) {
  // Fixed and adjustable period lengths
  const totalYears = parseInt(product.term);
  const fixedMonths = parseInt(product.timePeriodOfInitialRate);
  const fixedYears = Math.round(fixedMonths / 12);
  const adjustableMonths = totalYears * 12 - fixedYears * 12;

  // Payment amounts
  const fixedPayment = formatCurrency(product.payment, true);
  const min = parseFloat(product.minPaymentAfter1stRateChange);
  const max = parseFloat(product.maxPaymentAfterMaxRateChange);
  const minPayment = formatCurrency(min, true);
  const maxPayment = formatCurrency(max, true);

  const adjustablePaymentText =
    min < max
      ? `${adjustableMonths} monthly payments between ${minPayment} – ${maxPayment}`
      : `${adjustableMonths} monthly maximum payments of ${maxPayment}`;

  return (
    <>
      <div className={styles.payment}>
        <strong>Years 1 – {fixedYears}:</strong> {fixedMonths} monthly payments
        of {fixedPayment}
      </div>
      <div className={styles.payment}>
        <strong>
          Years {fixedYears + 1} – {totalYears}:
        </strong>{' '}
        {adjustablePaymentText}
      </div>
    </>
  );
}

const sharedRows = [
  {
    key: keys.LOAN_AMOUNT,
    label: 'Loan amount',
    type: types.CURRENCY,
    value: ({ loanAmount, loanPurpose, cashOutAmount }) => {
      return isRefinance(loanPurpose) ? loanAmount + cashOutAmount : loanAmount;
    }
  },
  { key: keys.RATE, label: 'Interest rate', type: types.PERCENT },
  { key: keys.APR, label: 'APR', type: types.PERCENT },
  { label: 'Loan term', value: ({ term }) => `${term} years` },
  {
    label: 'Down payment',
    value: ({ loanAmount, homeValue, loanPurpose, cashOutAmount }) => {
      if (isRefinance(loanPurpose)) {
        loanAmount += cashOutAmount;
      }

      return calculatePercent(homeValue - loanAmount, homeValue, false);
    }
  },
  {
    label: 'Loan-to-value (LTV)',
    value: ({ loanAmount, homeValue, loanPurpose, cashOutAmount }) => {
      if (isRefinance(loanPurpose)) {
        loanAmount += cashOutAmount;
      }

      return calculatePercent(loanAmount, homeValue, false);
    }
  },
  {
    label: 'Points',
    value: ({ closingCosts, points }) =>
      `${formatCurrency(closingCosts)} (${points.toFixed(3)})`
  }
];

const adjustableRows = [
  {
    label: 'Time period of initial rate',
    value: ({ timePeriodOfInitialRate: t }) => `${t} months`
  },
  {
    label: 'Rate change frequency after initial period',
    value: data => `${data.rateChangeFrequency} ${data.changeFrequencyUnits}`
  },
  {
    key: 'firstRateChangeCap',
    label: 'First interest rate change cap',
    type: types.PERCENT,
    addDecimals: false
  },
  {
    key: 'nextRateChangeCap',
    label: 'Subsequent interest rate change cap',
    type: types.PERCENT,
    addDecimals: false
  },
  {
    key: 'maxChangeCap',
    label: 'Maximum lifetime cap over initial rate',
    type: types.PERCENT,
    addDecimals: false
  }
];
